import { Icon, TextBadge, Tooltip } from '@scality/core-ui';
import { SingleSelectableContent } from '@scality/core-ui/dist/components/tablev2/SingleSelectableContent';
import type { Column } from '@scality/core-ui/dist/components/tablev2/Tablev2.component';
import { SmallerText } from '@scality/core-ui/dist/components/text/Text.component';
import { Box, Button, Table } from '@scality/core-ui/dist/next';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { type APIAlert, APIAlertSeverityEnum } from '../../../gen/openapi';
import { useChat } from '../../../hooks/useChat';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { InstanceValue } from '../../InstanceValue';
import { StatusDotError, StatusDotLoading, StatusDotWarning } from '../../welcome-page/Alerts';
import { NoData } from '../NoData';

// Here we hack into core ui table. The table height must
// be a fixed number of pixels in core-ui, and the rows height
// varies with the width of the table. So we enforce some value
// to be able to reliably display all the table rows.
const TableWrapper = styled.div<{ itemCount: number }>`
  height: ${(props) => (props.itemCount + 1) * 40 + 20}px;

  & * {
    font-size: 16px !important;
    line-height: 18px !important;
    word-break: break-word !important;
  }
  & div[role="columnheader"] * {
    white-space: nowrap !important;
    word-break: normal !important;
    overflow: hidden;
  }
  & div[role="row"] {
    height: 42px !important;
  }
`;

const AlertDomainCell = ({ value }: { value: string }) => {
  return (
    <div style={{ display: 'inline-block' }}>
      <TextBadge text={value} variant="infoPrimary" />
    </div>
  );
};

const GetDetailsCell = ({ alertId, submit }: { alertId: string; submit: (alertId: string) => void }) => {
  const handleClick = useCallback(() => submit(alertId), [alertId, submit]);

  return (
    <Button
      variant="secondary"
      size="inline"
      onClick={handleClick}
      icon={<Icon name="Arrow-right" />}
      label={'Get details'}
    />
  );
};

const getColumns = (submit: (alertId: string) => void): Column<APIAlert>[] => [
  {
    Header: 'Severity',
    accessor: 'severity',
    width: 96,
    Cell: ({ value }: { value: APIAlertSeverityEnum }) => {
      let icon: React.ReactNode;
      const iconSize = 24;
      if (value === APIAlertSeverityEnum.Low || value === APIAlertSeverityEnum.Medium) {
        icon = <StatusDotWarning size={iconSize} />;
      } else if (value === APIAlertSeverityEnum.High || value === APIAlertSeverityEnum.Critical) {
        icon = <StatusDotError size={iconSize} />;
      } else {
        icon = <StatusDotLoading size={iconSize} />;
      }
      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Tooltip overlay={(value && <SmallerText>{value}</SmallerText>) || null}>{icon}</Tooltip>
        </Box>
      );
    },
  },
  {
    Header: 'Alert',
    accessor: 'alert_domain',
    width: 160,
    dropAt: 680,
    Cell: AlertDomainCell,
  },
  {
    Header: 'Deployment',
    accessor: 'instance_id',
    // A badge carries a deployment's name, which is far wider than the id it replaced.
    width: 220,
    dropAt: 820,
    Cell: ({ value }: { value: string }) => <InstanceValue value={value} />,
  },
  {
    Header: 'Description',
    accessor: 'description',
    // Gives back most of what the deployment column took, so the row still fits a side panel.
    width: 280,
  },
  {
    Header: '',
    accessor: 'id',
    width: 130,
    Cell: ({ value }: { value: string }) => <GetDetailsCell alertId={value} submit={submit} />,
  },
];

export const AlertsList: AIToolComponent<APIAlert[]> = ({ params: alerts }) => {
  const { sendMessage } = useChat();

  const submit = useCallback(
    (alertId: string) => {
      sendMessage({ query: `Tell me more about this alert: ${alertId}` });
    },
    [sendMessage],
  );

  const columns = useMemo(() => getColumns(submit), [submit]);

  // An empty list is good news here, so it reads as an answer rather than as a gap.
  if (alerts.length === 0) {
    return <NoData title="Alerts" message="No alert over this time range." />;
  }

  return (
    <TableWrapper itemCount={alerts.length}>
      <Table columns={columns} data={alerts as any} revealDroppedColumns>
        <SingleSelectableContent rowHeight="h40" separationLineVariant="backgroundLevel3" />
      </Table>
    </TableWrapper>
  );
};

AlertsList.description = 'Displays a list of alerts with their details.';

AlertsList.schema = {
  type: 'array',
  items: {
    type: 'object',
    properties: {
      id: { type: 'string' },
      instance_id: { type: 'string' },
      alert_domain: { type: 'string' },
      created_at: { type: 'string', format: 'date-time' },
      alert_date: { type: 'string', format: 'date-time' },
      description: { type: 'string' },
      severity: { type: 'string', enum: ['Low', 'Medium', 'High', 'Critical'] },
      status: { type: 'string', enum: ['Active', 'Resolved'] },
    },
    required: ['id', 'instance_id', 'alert_domain', 'description'],
  },
  minItems: 1,
  maxItems: 100,
};

AlertsList.displayInPanel = true;
